import clsx from "clsx";
import PropTypes from "prop-types";
import * as React from "react";
import IconButton from "../../actions/Button/IconButton";
import { DismissIcon } from "../../primitives/Icons/Icons";
import Typography from "../../primitives/Typography/Typography";
import shared from "../../shared.module.css";
import Progress from "../Progress/Progress";
import css from "./UploadTray.module.css";

const TrayRow = ({ item, onCancel }) => (
  <li className={css.row}>
    {item.thumbnail && (
      <span className={css.thumb} aria-hidden="true">
        {item.thumbnail}
      </span>
    )}

    <div className={css.rowBody}>
      <div className={css.rowHeading}>
        <Typography variant="label-lg" as="span">
          {item.title}
        </Typography>
        <Typography variant="label-sm" color="variant" as="span">
          {item.note ?? item.subtitle}
        </Typography>
      </div>

      <Progress
        value={item.percent}
        label={`Upload progress for ${item.title}`}
      />
    </div>

    {onCancel && (
      <IconButton
        variant="text"
        size="sm"
        label={`Cancel upload for ${item.title}`}
        onClick={() => onCancel(item.id)}
      >
        <DismissIcon />
      </IconButton>
    )}
  </li>
);

TrayRow.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    // Replaces the subtitle once there is something more useful to say than
    // whose order it is — "Uploaded", say.
    note: PropTypes.string,
    thumbnail: PropTypes.node,
    percent: PropTypes.number,
  }).isRequired,
  onCancel: PropTypes.func,
};

// Presentational: it neither starts, tracks, nor cancels anything itself.
const UploadTray = React.forwardRef(
  ({ items, summary, onCancel, className, ...rest }, ref) => {
    if (items.length === 0) return null;

    return (
      <div ref={ref} className={clsx(css.root, className)} {...rest}>
        {/* The only live region: the count is what changes meaningfully, and
            announcing each percentage would talk over the rest of the page. The
            list and the summary below it are read by navigation instead — one of
            the two is always display:none, so neither is announced twice. */}
        <p className={shared.visuallyHidden} aria-live="polite">
          {summary.label}
        </p>

        <ul className={css.list}>
          {items.map((item) => (
            <TrayRow key={item.id} item={item} onCancel={onCancel} />
          ))}
        </ul>

        <div className={css.summary}>
          <Typography variant="label-lg" as="span">
            {summary.label}
          </Typography>
          <Progress value={summary.percent} label="Upload progress" />
        </div>
      </div>
    );
  },
);

UploadTray.displayName = "UploadTray";

UploadTray.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  summary: PropTypes.shape({
    label: PropTypes.string.isRequired,
    percent: PropTypes.number,
  }).isRequired,
  onCancel: PropTypes.func,
  className: PropTypes.string,
};

export default UploadTray;
